import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

// ── DTOs ────────────────────────────────────────────────────────────

export interface CostoItemDTO {
  nombre: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

export type ManoObraItemTipo = 'apu' | 'adicional' | 'servicio_varios' | 'descuento';

export interface ManoObraItemDTO {
  tipo: ManoObraItemTipo;
  apu_nombre: string | null;
  mdo_nombre: string | null;
  descripcion: string | null;
  categoria: string | null;
  cantidad: number | null;
  precio_unitario: number | null;
  subtotal: number; // negativo para descuentos
}

export interface PreliquidacionResumenDTO {
  total_proforma: number;
  total_anticipos: number;
  total_extras: number;           // informacional — ya incluido en costo_mano_obra
  total_gastos_adicionales: number;
  total_reingresos: number;
  costo_mano_obra: number;        // APUs + extras − descuentos
  costo_materiales: number;
  costo_insumos: number;
  costo_materiales_adicionales: number;
  costo_excedentes: number;
  costo_total: number;
  monto_ejecutado: number;
  monto_ejecutado_con_iva: number;
  margen: number;
  margen_porcentaje: number;
  items_mano_obra: ManoObraItemDTO[];
  items_materiales: CostoItemDTO[];
  items_materiales_adicionales: CostoItemDTO[];
  items_excedentes: CostoItemDTO[];
  items_insumos: CostoItemDTO[];
}

export interface GastoAdicionalDTO {
  id: string;
  categoria: string;
  descripcion: string;
  monto: number;
  notas: string | null;
  created_at: string;
}

export interface ReingresosDTO {
  id: string;
  categoria: string;
  descripcion: string;
  monto: number;
  notas: string | null;
  created_at: string;
}

export interface PreliquidacionDTO {
  id: string | null;
  project_id: string;
  estado: string | null;
  resumen: PreliquidacionResumenDTO;
  gastos_adicionales: GastoAdicionalDTO[];
  reingresos: ReingresosDTO[];
}

// ── Hooks ───────────────────────────────────────────────────────────

export function usePreliquidacion(projectId: string) {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/preliquidacion`);
      return (res.data as { data: PreliquidacionDTO }).data;
    },
    enabled: !!projectId,
  });
}

export function useCreatePreliquidacion(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notas?: string | undefined) => {
      const res = await api.post(`/projects/${projectId}/preliquidacion`, { notas: notas ?? null });
      return (res.data as { data: { id: string; estado: string } }).data;
    },
    // onSettled fires on both success AND error — always refresh so stale cache never hides existing preliq
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useAddGastoAdicional(preliquidacionId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      categoria: string;
      descripcion: string;
      monto: number;
      notas?: string;
    }) => {
      const res = await api.post(`/preliquidaciones/${preliquidacionId}/gastos`, data);
      return (res.data as { data: GastoAdicionalDTO }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useUpdateGastoAdicional(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      gastoId: string;
      categoria?: string;
      descripcion?: string;
      monto?: number;
      notas?: string | null;
    }) => {
      const { gastoId, ...body } = data;
      const res = await api.put(`/gastos/${gastoId}`, body);
      return (res.data as { data: GastoAdicionalDTO }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useDeleteGastoAdicional(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gastoId: string) => {
      await api.delete(`/gastos/${gastoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useAddReingreso(preliquidacionId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      categoria: string;
      descripcion: string;
      monto: number;
      notas?: string;
    }) => {
      const res = await api.post(`/preliquidaciones/${preliquidacionId}/reingresos`, data);
      return (res.data as { data: ReingresosDTO }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useUpdateReingreso(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      reingresosId: string;
      categoria?: string;
      descripcion?: string;
      monto?: number;
      notas?: string | null;
    }) => {
      const { reingresosId, ...body } = data;
      const res = await api.put(`/reingresos/${reingresosId}`, body);
      return (res.data as { data: ReingresosDTO }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useDeleteReingreso(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reingresosId: string) => {
      await api.delete(`/reingresos/${reingresosId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useCompletePreliquidacion(preliquidacionId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.put(`/preliquidaciones/${preliquidacionId}/complete`);
      return (res.data as { data: { id: string; estado: string } }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
    },
  });
}

export function useLiquidarProyecto(preliquidacionId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post(`/preliquidaciones/${preliquidacionId}/liquidar`);
      return (res.data as { data: { project_id: string; estado: string } }).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), 'preliquidacion'],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(projectId),
      });
    },
  });
}
